import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { reconcileApi } from './api'
import type { SubmitReconciliationPayload } from './types'

export function useReconcileAccounts() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reconcile', 'accounts'),
    queryFn: reconcileApi.accounts,
  })
}

export function useReconcileHistory(accountNo?: string) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reconcile', 'history', accountNo ?? 'all'),
    queryFn: () => reconcileApi.history(accountNo),
  })
}

export function useCalculateReconciliation() {
  return useMutation({
    mutationFn: (args: { accountNo: string; date: string }) =>
      reconcileApi.calculate(args.accountNo, args.date),
  })
}

export function useReconcileCustomerPayments(dateFrom: string | null, dateTo: string | null, enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reconcile', 'customer-payments', dateFrom ?? 'none', dateTo ?? 'none'),
    queryFn: () => reconcileApi.customerPayments(dateFrom, dateTo),
    enabled,
  })
}

export function useReconcileVendorPayments(dateFrom: string | null, dateTo: string | null, enabled: boolean) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'reconcile', 'vendor-payments', dateFrom ?? 'none', dateTo ?? 'none'),
    queryFn: () => reconcileApi.vendorPayments(dateFrom, dateTo),
    enabled,
  })
}

export function useSubmitReconciliation() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (payload: SubmitReconciliationPayload) => reconcileApi.submit(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reconcile') })
    },
  })
}
